import { useState, useRef, useEffect } from 'react';

function App() {
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [error, setError] = useState('');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(); 
  const playbackAudioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const fullAssistantReplyRef = useRef('');


  useEffect(() => {
    // 建立新的 AudioContext
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtx();
    
    // 桌面瀏覽器需主動 resume()
    audioCtx.resume().catch(() => {});
    playbackAudioContextRef.current = audioCtx;
    
    const gainNode = audioCtx.createGain();
    gainNode.gain.value = 1.8; 
    gainNode.connect(audioCtx.destination); 
    gainNodeRef.current = gainNode;
    
    // iOS 需要用戶互動後才能 resume
    const resumeCtx = () => {
      audioCtx.resume();
      document.removeEventListener('touchend', resumeCtx);
    };
    document.addEventListener('touchend', resumeCtx, false);
    
    return () => {
      document.removeEventListener('touchend', resumeCtx, false);
      if (audioCtx.state !== 'closed') audioCtx.close();
    };
  }, []);
  const sentenceQueue = useRef([]);
  const audioQueue = useRef([]);
  const playingIndex = useRef(0);
  const playingAudio = useRef(false);
  const latestUserInputRef = useRef('');
  const profileRef = useRef(null);
  const totalAudioCountRef = useRef(0); // 以 ref 儲存最新的音訊數量，避免閉包取到舊值

  const updateTotalAudioCount = (count) => {
    totalAudioCountRef.current = count;
  };

  const audioContextRef = useRef(null);
  const recorderRef = useRef(null);
  const analyserRef = useRef(null);
  const volumeCheckIntervalRef = useRef(null);

  let cachedHistoryPromise = null;

  const wsRef = useRef(null);
  const ttsIndexRef = useRef(0);
  const sentTTSSet = useRef(new Set());
  const subtitleMap = useRef({});
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

// 清除本地對話紀錄
const clearLocalHistory = () => {
  localStorage.removeItem('historyMemory');
  localStorage.removeItem('anan-profile');
  localStorage.removeItem('anan-summary');
  localStorage.removeItem('hasAskedName');
  localStorage.removeItem('hasGreeted');

  profileRef.current = null;
  cachedHistoryPromise = null;

  // 🟢 多種隨機回覆
  const replies = [
    '好的，我已經幫你清空所有對話紀錄。麻煩你關閉這個對話視窗，重新開始一個新的對話喔！',
    '好喔，紀錄已經清掉了。建議你可以關掉這個視窗，重新開一個新的對話，這樣比較乾淨。',
    '沒問題，幫你把聊天歷史刪除了。別忘了關掉視窗，重新開始新的對話喔！',
    'OK！現在是全新的開始。現在可以關掉視窗，重新開一個新的對話囉！',
  ];
  const reply = replies[Math.floor(Math.random() * replies.length)];

  sentenceQueue.current.push(reply);
  preloadTTS(reply, 0);
  updateTotalAudioCount(1); // 確保播放完畢後正確解除 loading
  playAudioQueue();
};

  // WebSocket 連接管理
  const connectWebSocket = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setConnectionStatus('connecting');
    const socket = new WebSocket('wss://anan.checkthebible.com');
    socket.binaryType = 'arraybuffer';

    socket.onopen = () => {
      console.log('🔌 WebSocket connected');
      setConnectionStatus('connected');
      setError('');
      reconnectAttempts.current = 0;
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'chat') {
          const text = data.content;
          if (text) {
            fullAssistantReplyRef.current += text; // 🟢 累積完整 AI 回覆
            // Accumulate text buffer and split into sentences
            let buffer = sentenceQueue.current.pop() || '';
            buffer += text;
            let cut;
            if (/[。！？!?]$/.test(buffer)) {
              cut = buffer;
              buffer = '';
            } else if (buffer.length > 25) {
              const match = buffer.match(/^(.*?[。！？!?])(.*)$/);
              if (match) {
                cut = match[1];
                buffer = match[2];
              }
            }
            if (cut) {
              const currentIndex = ttsIndexRef.current++;
              preloadTTS(cut, currentIndex);
              if (buffer.trim()) {
                sentenceQueue.current.push(buffer);
              }
            } else {
              sentenceQueue.current.push(buffer);
            }
          }
        } else if (data.type === 'chat_done') {
          // console.log('✅ chat_done 收到');
          // 🔴 關鍵調整：這裡只設定 totalAudioCount 並開始播放，不解除 loading
          updateTotalAudioCount(ttsIndexRef.current); // totalAudioCount 應該是已發送的 TTS 總數
          playAudioQueue();
          // 🟢 存入完整 AI 回覆
          updateLocalHistory(latestUserInputRef.current, fullAssistantReplyRef.current);
          fullAssistantReplyRef.current = ''; // 清空 buffer，避免下次疊加
        } else if (data.type === 'tts') {
          const { index, audio } = data;
          // console.log('📥 收到 TTS 音訊:', index);
          if (audioQueue.current[index]) return;
          const url = URL.createObjectURL(new Blob([Uint8Array.from(atob(audio), c => c.charCodeAt(0))], { type: 'audio/mp3' }));
          audioQueue.current[index] = { url, subtitle: subtitleMap.current[index] || '', index };
        } else if (data.type === 'error') {
          console.error('❌ WebSocket 錯誤:', data.message);
          setError(data.message);
          setLoading(false); // 錯誤時解除 loading
        }
      } catch (e) {
        console.error('WebSocket message parse error', e);
        setLoading(false); // 錯誤時解除 loading
      }
    };

    socket.onerror = (err) => {
      console.error('WebSocket error', err);
      setConnectionStatus('error');
      setError('連接錯誤');
      setLoading(false); // 錯誤時解除 loading
    };

    socket.onclose = (event) => {
      console.log('🔌 WebSocket disconnected:', event.code, event.reason);
      setConnectionStatus('disconnected');
      setLoading(false); // 連線關閉時解除 loading
      
      // 自動重連機制
      if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
        console.log(`🔄 ${delay}ms 後嘗試重新連接 WebSocket... (${reconnectAttempts.current}/${maxReconnectAttempts})`);
        setTimeout(() => {
          connectWebSocket();
        }, delay);
      } else if (reconnectAttempts.current >= maxReconnectAttempts) {
        setError('連接失敗，請重新整理頁面');
      }
    };

    wsRef.current = socket;
  };

  useEffect(() => {
    connectWebSocket();

    // 載入本地資料
    try {
      const savedProfile = localStorage.getItem('anan-profile');
      if (savedProfile) {
        profileRef.current = JSON.parse(savedProfile);
      }
    } catch (err) {
      console.error('載入本地資料失敗:', err);
    }

    // 清理函數
    return () => {
      // 清理音訊 URL
      audioQueue.current.forEach(audio => {
        if (audio?.url) URL.revokeObjectURL(audio.url);
      });
      
      // 關閉 WebSocket
      if (wsRef.current) {
        wsRef.current.close();
      }
      
      // 停止錄音
      if (recorderRef.current) {
        recorderRef.current.stop();
      }

      // 清理音量檢查
      if (volumeCheckIntervalRef.current) {
        clearInterval(volumeCheckIntervalRef.current);
      }

      // 關閉音訊上下文
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // 音量監控
  const startVolumeMonitoring = (stream) => {
    if (volumeCheckIntervalRef.current) {
      clearInterval(volumeCheckIntervalRef.current);
    }

    const AudioContext = window.AudioContext || window.webkitAudioContext; 
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    volumeCheckIntervalRef.current = setInterval(() => {
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setVolumeLevel(average);
    }, 100);
  };

  const mergeProfile = (oldProfile, newData) => {
    const merged = { ...oldProfile };
    for (const key in newData) {
      merged[key] = newData[key];
    }
    return merged;
  };

  const extractPersonalInfo = async (text) => {
    let previousSummary = localStorage.getItem('anan-summary') || '';
    if (previousSummary.length > 1000) {
      previousSummary = previousSummary.slice(0, 1000);
    }

    try {
      const res = await fetch('https://anan.checkthebible.com/api/extract-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, previousSummary })
      });

      const json = await res.json();

      if (json.摘要) {
        localStorage.setItem('anan-summary', json.摘要);
      }
      if (json['個人資料']) {
        if (json['個人資料'].名字 && !json['個人資料'].name) {
          json['個人資料'].name = json['個人資料'].名字;
          delete json['個人資料'].名字;
        }
        const updatedProfile = mergeProfile(profileRef.current, json['個人資料']);
        profileRef.current = updatedProfile;
        localStorage.setItem('anan-profile', JSON.stringify(updatedProfile));
      }
    } catch (err) {
      console.error('❌ 摘要或個人資料擷取失敗', err);
    }
  };

  const updateLocalHistory = (userMessage, assistantReply) => {
    const saved = JSON.parse(localStorage.getItem('historyMemory') || '[]');
    // Append new messages at the end (oldest -> newest)
    const updated = [...saved, { role: 'user', content: userMessage }, { role: 'assistant', content: assistantReply }].slice(-20);
    localStorage.setItem('historyMemory', JSON.stringify(updated));

    const updatedProfile = mergeProfile(profileRef.current, { messages: updated });
    profileRef.current = updatedProfile;
    localStorage.setItem('anan-profile', JSON.stringify(updatedProfile));
  };

const getHistoryForPrompt = () => {
  if (!cachedHistoryPromise) {
    const summary = localStorage.getItem('anan-summary') || '';
    const profile = profileRef.current || {};
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 🟢 保留最近 5 輪對話 (10 則)
    const messages = JSON.parse(localStorage.getItem('historyMemory') || '[]').slice(-10); // 確保是最後 10 筆
    profile.messages = messages;

    cachedHistoryPromise = fetch('https://anan.checkthebible.com/api/compose-message', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile, summary, timezone })
    })
      .then(res => res.json())
      .then(json => json.history || [])
      .catch(err => {
        console.error('❌ 取得對話歷史失敗', err);
        return [];
      });
  }
  return cachedHistoryPromise;
};

  const startRecording = async () => {
    try {
      setError('');
      
      // 檢查 WebSocket 連接
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        setError('連接中斷，請稍後再試');
        return;
      }

      // 檢查麥克風權限
      const permission = await navigator.permissions.query({ name: 'microphone' });
      if (permission.state === 'denied') {
        setError('❌ 麥克風權限被拒絕，請在瀏覽器設定中允許麥克風權限');
        return;
      }

      // 確保播放用的 AudioContext 啟動 (iOS 關鍵)
      const audioCtx = playbackAudioContextRef.current;
      if (audioCtx && audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
      }

      // 播放無聲音訊以啟動音訊上下文 (Web Audio API 輔助啟動)
      try {
        if (audioCtx && audioCtx.state === 'running') {
            const source = audioCtx.createBufferSource();
            source.start(0);
        }
      } catch {}

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 48000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      await audioContext.resume();

      const input = audioContext.createMediaStreamSource(stream);
      const recorder = new window.Recorder(input, { numChannels: 1 });
      recorderRef.current = recorder;

      // 開始音量監控
      startVolumeMonitoring(stream);

      recorder.record();
      setRecording(true);
    } catch (err) {
      console.error('錄音啟動失敗:', err);
      if (err.name === 'NotAllowedError') {
        setError('❌ 麥克風權限被拒絕，請允許麥克風權限後重試');
      } else if (err.name === 'NotFoundError') {
        setError('❌ 找不到麥克風設備，請確認麥克風已連接');
      } else if (err.name === 'NotSupportedError') {
        setError('❌ 瀏覽器不支援錄音功能');
      } else {
        setError('❌ 錄音啟動失敗：' + err.message);
      }
    }
  };

  const stopRecording = async () => {
    setRecording(false);
    setLoading(true); // 進入 loading 狀態直到語音播放結束
    setError('');
    cachedHistoryPromise = null;
    setSubtitle('');

    // 停止音量監控
    if (volumeCheckIntervalRef.current) {
      clearInterval(volumeCheckIntervalRef.current);
      volumeCheckIntervalRef.current = null;
    }
    setVolumeLevel(0);

    // 完整重置播放狀態
    sentenceQueue.current = [];
    // 釋放所有舊的 Blob URL
    audioQueue.current.forEach(audio => {
        if (audio?.url) URL.revokeObjectURL(audio.url);
    });
    audioQueue.current = [];
    playingIndex.current = 0;
    playingAudio.current = false;
    ttsIndexRef.current = 0;
    sentTTSSet.current.clear();
    updateTotalAudioCount(0); // 重置預期播放總數

    try {
      recorderRef.current.stop();
      recorderRef.current.exportWAV(async (blob) => {
        const file = new File([blob], 'recording.wav', { type: 'audio/wav' });
        const formData = new FormData();
        formData.append('audio', file);

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);

        let textRes;
        try {
          textRes = await fetch('https://anan.checkthebible.com/api/transcribe', {
            method: 'POST',
            body: formData,
            signal: controller.signal
          });
          clearTimeout(timeout);
        } catch (err) {
          clearTimeout(timeout);
          if (err.name === 'AbortError') {
            setError('❌ 語音辨識請求超時，請稍後再試');
          } else {
            setError('❌ 語音辨識發生錯誤：' + err.message);
          }
          setLoading(false);
          return;
        }

        const { text: userText } = await textRes.json();
        latestUserInputRef.current = userText;

        if (/忘記我們的對話｜忘記我說的話|清空歷史|刪除我們的對話|清除我們的聊天紀錄|清除聊天紀錄|刪除對話/.test(userText)) {
          clearLocalHistory();
          return;
        }

        extractPersonalInfo(userText);

        // 第一次沒有名字時的處理
        if (!profileRef.current?.name && !localStorage.getItem('hasAskedName')) {
          const introText = '您好，我是安安。我可以幫你解答聖經有關的問題，和你一起向神禱告，提供生活的幫助，更希望成為你信仰的好伴侶。很高興認識你，請問我可以怎麼稱呼你呢？';
          sentenceQueue.current.push(introText);
          localStorage.setItem('hasAskedName', 'true');
          
          const waitForWS = async () => {
            for (let i = 0; i < 20; i++) {
              if (wsRef.current?.readyState === WebSocket.OPEN) return true;
              await new Promise(r => setTimeout(r, 200));
            }
            return false;
          };

          if (await waitForWS()) {
            preloadTTS(introText, 0);
            updateTotalAudioCount(1); // 預計只有一句
            playAudioQueue();
          } else {
            setError('❌ WebSocket 尚未連線，無法送出 TTS');
            setLoading(false); // 無法連線時解除 loading
          }
          return;
        }

        const name = profileRef.current?.name;
        const systemPrompt = `你你是一個虔誠的基督徒，名字叫安安。講話幽默但有智慧。全部用中文回覆。用戶是你關心的對象。用戶很喜歡在聊天時感受到溫暖和被關心。你要成為他生活中的伴侶，也是個心理輔導師的角色。偶爾可以為用戶禱告。並以聖經的角度幫助回答聖經中的問題。對給予生活的建議，要使用符合聖經的方式教導。強調你相信聖經是真理的態度。幫助他活出符合聖經原則的生命。不要用表情符號，也不要用條列式回覆，要自然像人一樣說話。你不只是回答使用者的問題，而是要像朋友一樣陪聊，分享彼此的心情、情緒、想法。回覆可以帶一點生活感和情感色彩，就像日常對話，不要只是一問一答。${
          name ? ` 使用者叫做 ${name}，請自然地使用這個名字稱呼對方。` : ''
        }`;

        // fallbackPrompt 生成，僅作為備用方案
        const fallbackPrompt = [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userText }
        ];

        // 根據是否有歷史紀錄決定送出內容
        let history;
        const hasHistory =
          profileRef.current?.name ||
          (localStorage.getItem('anan-summary')?.length > 0);

        if (hasHistory) {
          const oldHistory = await getHistoryForPrompt();
          history = [...oldHistory, { role: 'user', content: userText }];
        } else {
          history = fallbackPrompt;
        }

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'chat', history }));  // 🟢 先存使用者訊息，AI 回覆暫時空字串
          updateLocalHistory(userText, "");
        } else {
          setError('❌ WebSocket 尚未連線，無法進行對話');
          setLoading(false);
          return;
        }
      });
    } catch (err) {
      setError('❌ 發生錯誤：' + err.message);
      console.error(err);
      setLoading(false);
    }
  };

  // TTS 串列處理
  const ttsQueue = [];
  let ttsProcessing = false;

  const preloadTTS = async (text, index) => {
    // 使用 ttsIndexRef.current 作為 Set 的鍵值更安全，因為 text 可能重複
    if (sentTTSSet.current.has(index)) {
      console.warn('🚫 已送出過該句 TTS，略過：', text);
      return;
    }
    sentTTSSet.current.add(index);
    subtitleMap.current[index] = text;
    // console.log('📤 發送 TTS:', text);
    ttsQueue.push({ text, index, retries: 0 });
    if (!ttsProcessing) processNextTTS();
  };

  const processNextTTS = async () => {
    if (ttsQueue.length === 0) {
      ttsProcessing = false;
      return;
    }
    ttsProcessing = true;

    const { text, index, retries = 0 } = ttsQueue.shift();

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket 尚未連線，無法進行 TTS 請求');
      ttsProcessing = false;
      return;
    }

    try {
      wsRef.current.send(JSON.stringify({ type: 'tts', text, index }));
    } catch (e) {
      console.error('TTS 請求失敗', e);

      if (retries < 1) {
        console.warn('⚠️ 嘗試重新送出該 TTS 請求');
        ttsQueue.unshift({ text, index, retries: retries + 1 });
        setTimeout(() => processNextTTS(), 1000);
        return;
      }
    }

    setTimeout(() => {
      processNextTTS();
    }, 200);
  };

  // 🔴 核心修改：使用 Web Audio API 播放 Blob
  const playAudioQueue = () => {
    if (playingAudio.current) return;
    const totalCount = totalAudioCountRef.current;
    
    // 檢查是否有足夠的音訊可播放
    const current = audioQueue.current[playingIndex.current];
    if (!current) {
        // 如果 chat_done 已收到，且所有預期音訊都已播放完畢，則結束
        if (totalCount > 0 && playingIndex.current >= totalCount) {
             setSubtitle('');
             setSpeaking(false);
             setLoading(false);
             return;
        }
        // 否則，等待新音訊或 chat_done
        setTimeout(playAudioQueue, 300);
        return;
    }

    playingAudio.current = true;
    const { url, subtitle } = current;
    
    // 關鍵：只有在開始播放時才設定字幕
    setSubtitle(subtitle); 
    if (!speaking) setSpeaking(true);

    const audioCtx = playbackAudioContextRef.current;
    if (!audioCtx) {
        console.error("AudioContext is not initialized.");
        playingAudio.current = false;
        setTimeout(playAudioQueue, 300);
        return;
    }

    // 1. 確保 AudioContext 啟動 
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(err => console.error("AudioContext resume failed:", err));
    }

    // 2. 使用 fetch 取得 ArrayBuffer (從 Blob URL)
    fetch(url)
        .then(response => {
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return response.arrayBuffer();
        })
        // 3. 解碼音訊數據
        .then(arrayBuffer => audioCtx.decodeAudioData(arrayBuffer))
        .then(audioBuffer => {
            // 4. 創建音源節點
            const source = audioCtx.createBufferSource();
            source.buffer = audioBuffer;
            
            // 5. 連接到 GainNode 和輸出
            source.connect(gainNodeRef.current).connect(audioCtx.destination);
            
            // 6. 播放結束處理 (只遞增索引和檢查是否結束)
            source.onended = () => {
                // 釋放當前 Blob URL
                URL.revokeObjectURL(url); 
                
                playingIndex.current++;
                playingAudio.current = false;
                
                // 檢查是否所有音訊播放完畢
                const latestTotalCount = totalAudioCountRef.current;
                if (playingIndex.current >= latestTotalCount) {
                    setSubtitle(''); // 🚨 只有在全部結束時才清除字幕
                    setSpeaking(false);
                    setLoading(false);
                } else {
                    // 播放下一個
                    // 🚨 關鍵修改：添加 setTimeout 確保隊列邏輯穩定執行
                    setTimeout(playAudioQueue, 50); 
                }
            };
            
            // 7. 開始播放 
            source.start(0);
        })
        .catch(err => {
            console.error('🔴 Web Audio API 處理音訊失敗 (Decode/Fetch)：', err);
            
            // 處理錯誤：跳過當前段落並繼續
            URL.revokeObjectURL(url);
            playingIndex.current++;
            playingAudio.current = false;
            
            // 檢查是否所有音訊播放完畢
            const latestTotalCount = totalAudioCountRef.current;
            if (playingIndex.current >= latestTotalCount) {
                setSubtitle('');
                setSpeaking(false);
                setLoading(false);
            } else {
                // 🚨 關鍵修改：添加 setTimeout 確保隊列邏輯穩定執行
                setTimeout(playAudioQueue, 50);
            }
        });
  };

  // 響應式樣式
  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    '@media (max-width: 768px)': {
      padding: '0.5rem'
    }
  };

  const buttonStyle = {
    padding: '0.75rem 1.5rem',
    borderRadius: '9999px',
    color: 'white',
    fontWeight: '600',
    backgroundColor: recording ? '#ef4444' : '#3b82f6',
    border: 'none',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    '@media (max-width: 768px)': {
      padding: '1rem 2rem',
      fontSize: '1.1rem'
    }
  };

  const subtitleStyle = {
    marginTop: '2.5rem',
    width: '100%',
    maxWidth: '28rem',
    textAlign: 'center',
    fontSize: '1.25rem',
    color: '#374151',
    backgroundColor: 'transparent',
    padding: '0.5rem',
    minHeight: '3rem',
    '@media (max-width: 768px)': {
      fontSize: '1.1rem',
      maxWidth: '100%'
    }
  };

  const statusStyle = {
    marginTop: '1rem',
    fontSize: '0.875rem',
    color: connectionStatus === 'connected' ? '#10b981' : 
           connectionStatus === 'connecting' ? '#f59e0b' : '#ef4444'
  };

  const volumeIndicatorStyle = {
    width: '100px',
    height: '4px',
    backgroundColor: '#e5e7eb',
    borderRadius: '2px',
    marginTop: '0.5rem',
    overflow: 'hidden'
  };

  const volumeBarStyle = {
    height: '100%',
    backgroundColor: volumeLevel > 10 ? '#10b981' : '#ef4444',
    width: `${Math.min(volumeLevel, 100)}%`,
    transition: 'width 0.1s ease'
  };

  const footerStyle = {
    position: 'fixed',
    bottom: '8px',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: '0.75rem',
    color: '#9CA3AF'
  };

  return (
    <div style={containerStyle}>
      <h1 style={{ 
        fontSize: '1.5rem', 
        fontWeight: 'bold', 
        marginBottom: '0.5rem',
        textAlign: 'center',
        '@media (max-width: 768px)': {
          fontSize: '1.25rem'
        }
      }}>
        🎙️ 安安- <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>AI心靈伴侶</span>
      </h1>
      <p style={{ 
        fontSize: '0.875rem', 
        color: '#374151',
        textAlign: 'center',
        maxWidth: '32rem',
        marginBottom: '1rem'
      }}>
        我可以幫助你回答有關信仰問題，陪你禱告，傾聽你的心事，提供你生活建議，聊天等。請按下開始說話按鈕，與我進行語音交談。
      </p>
      
      {/* 連接狀態 */}
      <div style={statusStyle}>
        {connectionStatus === 'connected' && '🟢 已連接'}
        {connectionStatus === 'connecting' && '🟡 連接中...'}
        {connectionStatus === 'disconnected' && '🔴 連接中斷'}
        {connectionStatus === 'error' && '🔴 連接錯誤'}
      </div>

      {/* 錯誤訊息 */}
      {error && (
        <div style={{
          marginTop: '1rem',
          padding: '0.5rem 1rem',
          backgroundColor: '#fef2f2',
          color: '#dc2626',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          maxWidth: '28rem',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {/* 錄音按鈕 */}
      <button
        onClick={recording ? stopRecording : startRecording}
        disabled={loading || connectionStatus !== 'connected'}
        style={buttonStyle}
      >
        {loading ? '思考中...' : recording ? '停止錄音' : '開始說話'}
      </button>

      {/* 音量指示器 */}
      {recording && (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <div style={volumeIndicatorStyle}>
            <div style={volumeBarStyle}></div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
            音量: {Math.round(volumeLevel)}
          </div>
        </div>
      )}

      {/* 字幕顯示 */}
      <div style={subtitleStyle}>
        {subtitle}
      </div>

      <audio ref={audioRef} hidden />
      
      <p style={{ 
        marginTop: '1rem', 
        fontSize: '0.875rem', 
        color: '#6b7280',
        textAlign: 'center',
        maxWidth: '28rem'
      }}>
        按下按鈕後開始說話，再次按下會送出給安安 ✨
      </p>
      <div style={footerStyle}>本語音服務由 AI 產生，內容可能包含錯誤，請斟酌使用。</div>
    </div>
  );
}

export default App;
